import { getDefaultRuntimeTheme } from './defaults'
import type { RuntimeTheme, ThemeTokens } from './types'

const toTrimmedString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const toScale = (value: unknown, fallback: number) => {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.max(0.75, Math.min(1.5, numeric))
}

const objectRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const normalizeThemeTokens = (value: unknown): ThemeTokens => {
  const source = objectRecord(value)
  const colorsSource = objectRecord(source.colors)
  const typographySource = objectRecord(source.typography)
  const radiusSource = objectRecord(source.radius)
  const spacingSource = objectRecord(source.spacing)
  const fallback = getDefaultRuntimeTheme()

  return {
    colors: {
      primary: toTrimmedString(colorsSource.primary) || fallback.tokens.colors.primary,
      background: toTrimmedString(colorsSource.background) || fallback.tokens.colors.background,
      surface: toTrimmedString(colorsSource.surface) || fallback.tokens.colors.surface,
      text: toTrimmedString(colorsSource.text) || fallback.tokens.colors.text,
      muted: toTrimmedString(colorsSource.muted) || fallback.tokens.colors.muted,
      border: toTrimmedString(colorsSource.border) || fallback.tokens.colors.border,
      accent: toTrimmedString(colorsSource.accent) || fallback.tokens.colors.accent,
    },
    typography: {
      fontFamilyBase:
        toTrimmedString(typographySource.fontFamilyBase) || fallback.tokens.typography.fontFamilyBase,
      fontFamilyHeading:
        toTrimmedString(typographySource.fontFamilyHeading) || fallback.tokens.typography.fontFamilyHeading,
      fontScaleBase: toScale(typographySource.fontScaleBase, fallback.tokens.typography.fontScaleBase),
    },
    radius: {
      sm: toTrimmedString(radiusSource.sm) || fallback.tokens.radius.sm,
      md: toTrimmedString(radiusSource.md) || fallback.tokens.radius.md,
      lg: toTrimmedString(radiusSource.lg) || fallback.tokens.radius.lg,
      xl: toTrimmedString(radiusSource.xl) || fallback.tokens.radius.xl,
    },
    spacing: {
      xs: toTrimmedString(spacingSource.xs) || fallback.tokens.spacing.xs,
      sm: toTrimmedString(spacingSource.sm) || fallback.tokens.spacing.sm,
      md: toTrimmedString(spacingSource.md) || fallback.tokens.spacing.md,
      lg: toTrimmedString(spacingSource.lg) || fallback.tokens.spacing.lg,
      xl: toTrimmedString(spacingSource.xl) || fallback.tokens.spacing.xl,
    },
  }
}

/**
 * Normalizes unknown theme payload into a safe `RuntimeTheme` shape.
 *
 * @param value Untrusted API/cache payload.
 * @returns Sanitized runtime theme with fallback defaults.
 */
export const normalizeRuntimeTheme = (value: unknown): RuntimeTheme => {
  const source = objectRecord(value)
  const mode = toTrimmedString(source.mode).toLowerCase() === 'dark' ? 'dark' : 'light'
  const status = toTrimmedString(source.status).toLowerCase() === 'draft' ? 'draft' : 'published'
  const fallback = getDefaultRuntimeTheme()

  return {
    id: toTrimmedString(source.id) || fallback.id,
    key: toTrimmedString(source.key) || fallback.key,
    name: toTrimmedString(source.name) || fallback.name,
    mode,
    status,
    isActive: source.isActive === true,
    tokens: normalizeThemeTokens(source.tokens),
  }
}
